import React, { useEffect, useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import {
  Calendar,
  ArrowRight,
  Play,
  MessageCircle,
  Clock,
  MapPin,
  Mail,
  Check,
} from "lucide-react";
import { toast } from "sonner";

const ASSETS = {
  video:
    "https://customer-assets.emergentagent.com/job_13ebe5a3-9399-422c-bf0f-2f47e3afdd06/artifacts/uqcey6lo_VideoPegasus.mp4",
  heroMachine:
    "https://customer-assets.emergentagent.com/job_pegasus-tech-1/artifacts/krwhie8z_maquina.png",
  quetzal:
    "https://customer-assets.emergentagent.com/job_13ebe5a3-9399-422c-bf0f-2f47e3afdd06/artifacts/lrv9jktd_pegasus-logo-quetzalcoatl.webp",
  counter:
    "https://customer-assets.emergentagent.com/job_13ebe5a3-9399-422c-bf0f-2f47e3afdd06/artifacts/uay42ktg_fondo-naza-1.webp",
  logo:
    "https://customer-assets.emergentagent.com/job_pegasus-tech-1/artifacts/ht77xccy_PEGASUSlogo.png",
  logoFooter:
    "https://customer-assets.emergentagent.com/job_pegasus-tech-1/artifacts/ikobwbrd_contornoblanco.png",
  headerBanner:
    "https://customer-assets.emergentagent.com/job_pegasus-tech-1/artifacts/624vydmx_banner-comprar-3.png",
  ofertaBanner:
    "https://customer-assets.emergentagent.com/job_pegasus-tech-1/artifacts/rm44m6ex_banerfinal.png",
  comboBanner:
    "https://customer-assets.emergentagent.com/job_pegasus-tech-1/artifacts/lcpele15_banner-comprar-4.png",
  logoCitec:
    "https://customer-assets.emergentagent.com/job_pegasus-tech-1/artifacts/hlbpce9t_FAMILIA%20LOGO%20PEGASUS%202025.png",
};

const WHATSAPP_NUMBER = "5213313868777"; // +52 1 33 1386 8777
const SHEETS_WEBHOOK_URL =
  "https://script.google.com/macros/s/AKfycbw4kJMLJ1HtoVVfxfJmd2lTddFod7Wnh_cI_6xajMqLdPpbnElYQvOdEtXTI0zBIi_l/exec";

// Target: Jueves 21 de mayo, 5:00 pm hora México (UTC-6) → 23:00 UTC, +36h
const getLiveEndDate = () => {
  const now = new Date();
  const EXTRA_HOURS = 36;
  // 17:00 Mexico = 23:00 UTC
  for (let year = now.getUTCFullYear(); year <= now.getUTCFullYear() + 5; year++) {
    const base = new Date(Date.UTC(year, 4, 21, 23, 0, 0)); // month 4 = May
    const utc = new Date(base.getTime() + EXTRA_HOURS * 3600 * 1000);
    const mxBase = new Date(base.getTime() - 6 * 3600 * 1000);
    if (
      mxBase.getUTCDate() === 21 &&
      mxBase.getUTCDay() === 4 && // 4 = Thursday
      utc.getTime() > now.getTime()
    ) {
      return utc;
    }
  }
  return null;
};

const Countdown = () => {
  const target = React.useMemo(() => getLiveEndDate(), []);
  const [diff, setDiff] = useState(() =>
    target ? target.getTime() - Date.now() : 0
  );

  useEffect(() => {
    if (!target) return;
    const id = setInterval(() => {
      setDiff(target.getTime() - Date.now());
    }, 1000);
    return () => clearInterval(id);
  }, [target]);

  const ended = !target || diff <= 0;
  const totalSec = Math.max(0, Math.floor(diff / 1000));
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;

  const pad = (n) => String(n).padStart(2, "0");

  const cells = [
    { v: pad(days), l: "Días" },
    { v: pad(hours), l: "Horas" },
    { v: pad(minutes), l: "Minutos" },
    { v: pad(seconds), l: "Segundos" },
  ];

  // unused dateLabel intentionally omitted

  const ended2 = ended;

  return (
    <div className="pg-countdown-card" data-testid="countdown-section">
      <div className="grid grid-cols-4 gap-3 sm:gap-5" data-testid="countdown-grid">
        {cells.map((c) => (
          <div key={c.l} className="pg-cd-cell" data-testid={`cd-${c.l.toLowerCase()}`}>
            <div className="pg-cd-num">{c.v}</div>
            <div className="pg-cd-label">{c.l}</div>
          </div>
        ))}
      </div>

      {!ended2 && (
        <div className="mt-7 sm:mt-8 text-center">
          <a
            href="https://mpago.la/14Zdv1g"
            target="_blank"
            rel="noopener noreferrer"
            className="pg-btn-primary"
            data-testid="cd-cta"
          >
            COMPRAR AHORA
            <ArrowRight className="arrow w-5 h-5" />
          </a>
        </div>
      )}
    </div>
  );
};

const useReveal = () => {
  useEffect(() => {
    const els = document.querySelectorAll(".pg-reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
};

const Section = ({ children, className = "", id }) => (
  <section id={id} className={`relative ${className}`}>
    <div className="mx-auto max-w-6xl px-5 sm:px-8 py-20 sm:py-28">{children}</div>
  </section>
);

const Landing = () => {
  useReveal();

  const [cupos, setCupos] = useState(94);
  const videoRef = useRef(null);
  const [form, setForm] = useState({ nombre: "", tel: "", ciudad: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  // gentle decrement of cupos for urgency (visual only)
  useEffect(() => {
    const id = setInterval(() => {
      setCupos((c) => (c > 78 ? c - 1 : c));
    }, 22000);
    return () => clearInterval(id);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim() || !form.tel.trim() || !form.ciudad.trim()) {
      toast.error("Completa los campos obligatorios");
      return;
    }
    setSubmitting(true);
    try {
      await Promise.allSettled([
        emailjs.send(
          process.env.REACT_APP_EMAILJS_SERVICE_ID,
          process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
          {
            nombre: form.nombre,
            telefono: form.tel,
            ciudad: form.ciudad,
            fecha: new Date().toLocaleString("es-MX", { timeZone: "America/Mexico_City" }),
          },
          { publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY }
        ),
        fetch(SHEETS_WEBHOOK_URL, {
          method: "POST",
          mode: "no-cors",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            nombre: form.nombre,
            telefono: form.tel,
            ciudad: form.ciudad,
            origen: window.location.pathname,
          }),
        }),
      ]);
      setSent(true);
      setForm({ nombre: "", tel: "", ciudad: "" });
    } catch (err) {
      console.error("EmailJS error:", err);
      toast.error("No pudimos enviar tu registro", {
        description: "Por favor inténtalo nuevamente.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const waLink = `https://wa.me/${WHATSAPP_NUMBER}`;

  return (
    <div data-testid="pegasus-landing" className="overflow-x-hidden">
      {/* URGENCY BAR */}
      <div className="pg-urgency" data-testid="urgency-bar">
        <span className="pulse" />
        ⚡ Campaña flash · Solo durante el live <span className="opacity-50 mx-2">·</span>
        Jueves 21 de mayo · 5:00 pm hora México
      </div>

      {/* NAV */}
      <nav className="pg-nav" data-testid="main-nav">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 h-16 flex items-center justify-between">
          <a href="#" className="flex items-center" data-testid="nav-logo" aria-label="Pegasus Hidrogel Shield">
            <img
              src={ASSETS.logo}
              alt="Pegasus Hidrogel Shield"
              className="h-12 w-auto object-contain"
              loading="eager"
            />
          </a>
          <div className="hidden md:flex items-center gap-7 text-sm font-medium text-[var(--pg-graphite)]">
            <a href="#video" className="hover:text-[var(--pg-orange)] transition-colors">Video</a>
            <a href="#oferta" className="hover:text-[var(--pg-orange)] transition-colors">Oferta</a>
          </div>
          <a
            href="https://mpago.la/14Zdv1g"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cta"
            className="hidden sm:inline-flex pg-btn-primary text-sm py-3 px-5"
          >
            COMPRAR AHORA
            <ArrowRight className="arrow w-4 h-4" />
          </a>
        </div>
      </nav>

      {/* COUNTDOWN — antes de la imagen principal */}
      <section className="bg-white pt-10 pb-6 sm:pt-14 sm:pb-8">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Countdown />
        </div>
      </section>

      {/* HEADER BANNER */}
      <div className="bg-white">
        <a
          href="https://mpago.la/14Zdv1g"
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full bg-white"
          data-testid="header-banner"
          aria-label="Comprar combo Pegasus ahora"
        >
          <img
            src={ASSETS.headerBanner}
            alt="Pegasus × CITEC — Comprar ahora"
            className="block w-full h-auto max-h-[680px] object-contain mx-auto"
            loading="eager"
          />
        </a>
      </div>

      {/* OFERTA — Banner imagen */}
      <section id="oferta" className="bg-[var(--pg-soft)] py-8 sm:py-12">
        <a
          href="#registro"
          data-testid="oferta-banner"
          aria-label="Llévate el súper combo Pegasus — Compra ahora"
          className="block w-full"
        >
          <img
            src={ASSETS.ofertaBanner}
            alt="¡Llévate el súper combo Pegasus! — Incluido en el combo"
            className="block w-full h-auto max-h-[680px] object-contain mx-auto"
            loading="lazy"
          />
        </a>

        <a
          href="https://mpago.la/14Zdv1g"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="combo-banner"
          aria-label="Incluido en el combo — Precio combo $8,900 MXN"
          className="block w-full mt-6 sm:mt-10"
        >
          <img
            src={ASSETS.comboBanner}
            alt="Incluido en el combo — Máquina Pegasus, micas y entradas CITEC"
            className="block w-full h-auto max-h-[680px] object-contain mx-auto"
            loading="lazy"
          />
        </a>
      </section>

      {/* VIDEO */}
      <Section id="video" className="bg-white">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="pg-label pg-reveal">Conoce la oportunidad</span>
          <h2 className="pg-h2 mt-5 pg-reveal">
            <span className="pg-text-orange">Pegasus</span> la máquina de hidrogel<br />
            más vendida en México
          </h2>
        </div>

        <div className="pg-reveal pg-video relative" data-testid="video-frame">
          <video
            ref={videoRef}
            src={ASSETS.video}
            autoPlay
            loop
            muted
            playsInline
            controls
            preload="auto"
            className="w-full h-full"
            data-testid="video-player"
          />
        </div>
      </Section>

      {/* BENEFITS */}
      <div className="bg-white border-y border-[var(--pg-line)]">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pg-reveal">
            {[
              { num: "100", unit: "", text: "Unidades disponibles\nen esta edición" },
              { num: "60", unit: "min", text: "Duración del live\nexclusivo en vivo" },
              { num: "2", unit: "", text: "Boletos CITEC\nincluidos en el paquete" },
              { num: "1", unit: "×", text: "Oportunidad única\nsolo durante la transmisión" },
            ].map((b, i) => (
              <div key={i} className="pg-benefit-item" data-testid={`benefit-${i}`}>
                <div className="pg-benefit-num flex items-baseline gap-1.5">
                  <span>{b.num}</span>
                  {b.unit && (
                    <span className="pg-benefit-unit">{b.unit}</span>
                  )}
                </div>
                <div className="text-sm text-[var(--pg-slate)] mt-3 whitespace-pre-line leading-relaxed">
                  {b.text}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FORM — Reserva tu combo */}
      <div className="pg-form-section" id="registro">
        <div className="mx-auto max-w-3xl px-5 sm:px-8 py-20 sm:py-28">
          <div className="text-center mb-10">
            <h2 className="pg-h2 pg-reveal">
              <span className="pg-text-orange">Reserva tu combo</span>
            </h2>
            <p className="mt-5 text-[var(--pg-slate)] max-w-xl mx-auto pg-reveal">
              Solo los registrados tienen acceso prioritario durante la preventa del live.
            </p>
          </div>

          <div className="pg-form-card pg-reveal">
            {!sent ? (
              <form onSubmit={handleSubmit} data-testid="registro-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="sm:col-span-2">
                    <label className="pg-label-form">Nombre completo *</label>
                    <input
                      className="pg-input"
                      type="text"
                      placeholder="Tu nombre completo"
                      value={form.nombre}
                      onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                      data-testid="input-nombre"
                      required
                    />
                  </div>
                  <div>
                    <label className="pg-label-form">Teléfono / WhatsApp *</label>
                    <input
                      className="pg-input"
                      type="tel"
                      placeholder="10 dígitos"
                      value={form.tel}
                      onChange={(e) => setForm({ ...form, tel: e.target.value })}
                      data-testid="input-tel"
                      required
                    />
                  </div>
                  <div>
                    <label className="pg-label-form">Ciudad *</label>
                    <input
                      className="pg-input"
                      type="text"
                      placeholder="¿Desde dónde nos ves?"
                      value={form.ciudad}
                      onChange={(e) => setForm({ ...form, ciudad: e.target.value })}
                      data-testid="input-ciudad"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="pg-btn-primary w-full justify-center mt-7 py-5 disabled:opacity-70 disabled:cursor-not-allowed"
                  data-testid="submit-btn"
                >
                  {submitting ? "Enviando…" : "Reserva tu combo"}
                  {!submitting && <ArrowRight className="arrow w-5 h-5" />}
                </button>

                <p className="mt-5 text-xs text-center text-[var(--pg-mute)] leading-relaxed">
                  Combos sujetos a disponibilidad durante la transmisión. Solo 100 combos disponibles.
                </p>
              </form>
            ) : (
              <div className="pg-success" data-testid="success-card">
                <div className="w-16 h-16 rounded-full bg-[var(--pg-orange-50)] mx-auto flex items-center justify-center mb-5">
                  <Check className="w-8 h-8 pg-text-orange" strokeWidth={2.5} />
                </div>
                <h3 className="font-display text-2xl sm:text-3xl font-extrabold text-[var(--pg-ink)]">
                  Registro enviado con éxito
                </h3>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <footer className="pg-footer" data-testid="footer">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <div className="grid md:grid-cols-3 gap-10 mb-10">
            <div>
              <img
                src={ASSETS.logoFooter}
                alt="Pegasus Hidrogel Shield"
                className="h-20 w-auto object-contain mb-5"
              />
              <p className="text-sm leading-relaxed">
                La primera máquina de hidrogel libre del mundo. Patrocinador oficial CITEC 2026.
              </p>
            </div>
            <div>
              <div className="text-white font-semibold mb-4 text-sm uppercase tracking-[0.16em]">Evento</div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2"><Calendar className="w-4 h-4 pg-text-orange" /> Jueves 21 de mayo · Facebook Live</li>
                <li className="flex items-center gap-2"><Clock className="w-4 h-4 pg-text-orange" /> 5:00 pm hora México</li>
                <li className="flex items-center gap-2"><MapPin className="w-4 h-4 pg-text-orange" /> Transmisión en línea</li>
              </ul>
            </div>
            <div>
              <div className="text-white font-semibold mb-4 text-sm uppercase tracking-[0.16em]">Contacto</div>
              <ul className="space-y-2 text-sm">
                <li>
                  <a href={waLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[var(--pg-orange)] transition-colors">
                    <MessageCircle className="w-4 h-4 pg-text-orange" /> WhatsApp +52 1 33 1386 8777
                  </a>
                </li>
                <li className="flex items-center gap-2"><Mail className="w-4 h-4 pg-text-orange" /> hola@pegasushidrogel.com</li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-white/8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
            <div>© {new Date().getFullYear()} Pegasus Hidrogel Shield. Todos los derechos reservados.</div>
            <div>Edición limitada · 100 unidades · Línea Ejecutiva</div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
